import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { notificationAPI, userAPI } from '../services/api';
import { showError, showInfo } from '../utils/toast';
import UserNotifications from './UserNotifications';
import DriverLoginModal from './DriverLoginModal';
import LanguageSelector from './LanguageSelector';
import { FaSun, FaMoon, FaUserCircle, FaHome, FaTaxi, FaMapMarkerAlt, FaBell } from 'react-icons/fa';

const Navbar = ({ user, onLogout, theme, toggleTheme = () => console.warn('toggleTheme function not provided') }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [_notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isDriver, setIsDriver] = useState(false);
  const [userNotifications, setUserNotifications] = useState([]);
  const [showUserNotifications, setShowUserNotifications] = useState(false);
  const [showDriverModal, setShowDriverModal] = useState(false);
  const isDark = theme === 'dark';

  // Define fetchUserNotifications with error handling to prevent initialization issues
  const fetchUserNotifications = useCallback(async (email) => {
    try {
      // Validate email before making API call
      if (!email || !email.includes('@')) {
        console.warn('⚠️ Invalid user email, skipping user notification fetch');
        return;
      }
      
      // Check if userAPI is properly initialized
      if (!userAPI || typeof userAPI.getNotifications !== 'function') {
        console.warn('⚠️ userAPI not properly initialized, skipping notification fetch');
        return;
      }
      
      const response = await userAPI.getNotifications(email);
      setUserNotifications(response.data?.notifications || response.data || []);
    } catch (error) {
      console.error('Error fetching user notifications:', error);
      // Graceful degradation - silently fail for user notifications
      if (error.response?.status === 403) {
        console.warn('⚠️ Unauthorized user notification access');
      }
      // Don't show errors to avoid notification spam
    }
  }, []);

  // Define fetchNotifications with error handling
  const fetchNotifications = useCallback(async (email) => {
    try {
      // Validate email before making API call
      if (!email || !email.includes('@')) {
        console.warn('⚠️ Invalid driver email, skipping notification fetch');
        return;
      }
      
      // Check if notificationAPI is properly initialized
      if (!notificationAPI || typeof notificationAPI.getNotifications !== 'function') {
        console.warn('⚠️ notificationAPI not properly initialized, skipping notification fetch');
        return;
      }
      
      const response = await notificationAPI.getNotifications(email);
      setNotifications(response.data);
      const newUnreadCount = response.data.filter(n => !n.is_read).length;
      
      // Show notification if there are new unread messages
      if (newUnreadCount > unreadCount && unreadCount > 0) {
        showInfo(`🔔 You have ${newUnreadCount} new notification${newUnreadCount > 1 ? 's' : ''}!`);
      }
      
      setUnreadCount(newUnreadCount);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      // Graceful degradation - don't show errors for guest users
      if (error.response?.status === 403) {
        console.warn('⚠️ Unauthorized notification access - user may not be logged in as driver');
        // Don't clear driver session on 403 errors, just reset notifications
        setNotifications([]);
        setUnreadCount(0);
      } else if (error.response?.status === 404) {
        showError('❌ Driver email not found. Please check your email.');
      }
      // Don't show errors for network issues to avoid spam
    }
  }, [unreadCount, showInfo, showError]);

  // Debug function to force show notifications
  const _forceShowNotifications = () => {
    console.log('Force showing notifications');
    setShowUserNotifications(true);
  };

  useEffect(() => {
    // Only fetch user notifications if we have a valid logged-in user
    if (user && user.email && user.email.includes('@')) {
      fetchUserNotifications(user.email);
      const interval = setInterval(() => fetchUserNotifications(user.email), 30000);
      return () => clearInterval(interval);
    }
  }, [user, fetchUserNotifications]);

  useEffect(() => {
    const driverEmail = localStorage.getItem('driverEmail');
    // Only fetch notifications if we have a valid driver email
    if (driverEmail && driverEmail.includes('@')) {
      setIsDriver(true);
      fetchNotifications(driverEmail);
      const interval = setInterval(() => fetchNotifications(driverEmail), 30000);
      return () => clearInterval(interval);
    } else if (driverEmail) {
      // Clean up invalid driver email
      console.warn('⚠️ Found invalid driver email in localStorage, removing:', driverEmail);
      localStorage.removeItem('driverEmail');
    }

    // Listen for driver status changes
    const handleDriverStatusChange = (event) => {
      const { email, status } = event.detail;
      const currentDriverEmail = localStorage.getItem('driverEmail');
      
      if (currentDriverEmail === email && status === 'approved') {
        setIsDriver(true);
        fetchNotifications(email);
      }
    };

    window.addEventListener('driverStatusChanged', handleDriverStatusChange);
    return () => window.removeEventListener('driverStatusChanged', handleDriverStatusChange);
  }, [fetchNotifications]);

  const handleDriverLogin = (email, token, driverData) => {
    // Ensure email is a string and has a valid format
    const emailStr = String(email || '').trim();
    
    if (!emailStr) {
      console.error('Invalid email provided for driver login');
      showError('❌ Invalid email address');
      return;
    }
    
    console.log('Driver login successful:', emailStr);
    
    // Store the email and token
    localStorage.setItem('driverEmail', emailStr);
    if (token) {
      localStorage.setItem('driverToken', token);
    }
    
    // Store driver data if available
    if (driverData) {
      localStorage.setItem('driverData', JSON.stringify(driverData));
    }
    
    // Update UI state
    setIsDriver(true);
    setShowDriverModal(false);
    
    // Only fetch notifications if we have a valid email
    if (emailStr.includes('@')) {
      fetchNotifications(emailStr);
    } else {
      console.warn('Driver login with invalid email format:', emailStr);
    }
    
    // Dispatch event to notify other components
    window.dispatchEvent(new CustomEvent('driverStatusChanged', { 
      detail: { 
        status: 'approved',
        email: emailStr
      } 
    }));
    
    // Show success message
    showInfo('🚗 Welcome back! You are now logged in as a driver.');
  };

  const handleDriverLogout = () => {
    // Clear all driver-related data from localStorage
    localStorage.removeItem('driverEmail');
    localStorage.removeItem('driverToken');
    
    // Reset driver state
    setIsDriver(false);
    setNotifications([]);
    setUnreadCount(0);
    
    // Close any open modals
    setShowDriverModal(false);
    setShowUserMenu(false);
    
    // Show success message
    showInfo('👋 Driver logged out successfully!');
    
    // Force UI update
    window.dispatchEvent(new CustomEvent('driverStatusChanged', { 
      detail: { 
        status: 'logged_out',
        email: null
      } 
    }));
  };

  const handleNotificationClick = () => {
    if (user) {
      // For logged-in users, navigate to messages page
      navigate('/messages');
    } else {
      // For non-logged-in users, redirect to login
      navigate('/login');
    }
  };

  // Mobile navigation items with improved styling
  const mobileNavItems = [
    { 
      to: "/", 
      icon: <FaHome className="text-2xl text-gray-700 group-hover:text-yellow-600 transition-colors" />, 
      label: 'Home',
      className: "flex-1 flex flex-col items-center justify-center group p-2 rounded-xl hover:bg-yellow-50 transition-colors"
    },
    { 
      to: "/services", 
      icon: <FaTaxi className="text-2xl text-gray-700 group-hover:text-yellow-600 transition-colors" />, 
      label: 'Services',
      className: "flex-1 flex flex-col items-center justify-center group p-2 rounded-xl hover:bg-yellow-50 transition-colors"
    },
    { 
      to: "/booking", 
      icon: (
        <div className="flex flex-col items-center justify-center group">
          <div className="relative">
            <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 p-3.5 rounded-full shadow-lg transform transition-all duration-300 group-hover:scale-110 group-hover:shadow-xl group-active:scale-95">
              <FaTaxi className="text-xl text-white" />
            </div>
          </div>
          <div className="mt-1.5">
            <span className="text-[11px] font-medium text-gray-700 whitespace-nowrap">
              Book Now
            </span>
          </div>
        </div>
      ),
      label: 'Book Now',
      isPrimary: true,
      className: "flex-1 flex flex-col items-center justify-center py-1"
    },
    { 
      to: "/tracking", 
      icon: <FaMapMarkerAlt className="text-2xl text-gray-700 group-hover:text-yellow-600 transition-colors" />, 
      label: 'Tracking',
      className: "flex-1 flex flex-col items-center justify-center group p-2 rounded-xl hover:bg-yellow-50 transition-colors"
    },
    { 
      to: "/dashboard", 
      icon: <FaUserCircle className="text-2xl text-gray-700 group-hover:text-yellow-600 transition-colors" />, 
      label: 'Dashboard',
      className: "flex-1 flex flex-col items-center justify-center group p-2 rounded-xl hover:bg-yellow-50 transition-colors"
    },
    {
      type: 'user',
      icon: user ? (
        <div className="relative">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-white font-bold shadow-md transform transition-transform hover:scale-105">
            {user.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}
          </div>
          {userNotifications.length > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
              {userNotifications.length}
            </span>
          )}
        </div>
      ) : (
        <div className="p-2 rounded-xl bg-yellow-50 group-hover:bg-yellow-100 transition-colors">
          <FaUserCircle className="text-2xl text-yellow-600 group-hover:text-yellow-700 transition-colors" />
        </div>
      ),
      label: user ? 'Account' : 'Login', // Use direct label instead of translation key
      className: 'flex items-center justify-center p-2',
      hideLabel: true,
      onClick: (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Only toggle menu if user is logged in
        if (user) {
          setShowUserMenu(!showUserMenu);
        } else {
          // If not logged in, navigate to login page
          navigate('/login');
        }
      },
      to: user ? '#' : '/login'
    }
  ];

  // Animation for the welcome header
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  
  // Hide header on scroll down, show on scroll up
  useEffect(() => {
    let lastScrollY = window.scrollY;
    
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY) {
        // Scrolling down
        setIsHeaderVisible(false);
      } else {
        // Scrolling up
        setIsHeaderVisible(true);
      }
      lastScrollY = currentScrollY;
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="relative">
      <nav className="relative">
        {/* Mobile Welcome Header - Extra Compact */}
        <div className={`md:hidden fixed top-0 left-0 right-0 bg-gradient-to-r from-yellow-200 to-yellow-300 shadow-md z-40 transition-transform duration-300 ${
          isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
        }`}>
          <div className="w-full px-4 py-2">
            <div className="flex items-center justify-between w-full">
              {/* Left side - Logo */}
              <div className="flex-shrink-0">
                <Link to="/" className="flex items-center">
                  <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center shadow-sm">
                    <FaTaxi className="text-yellow-800 text-lg" />
                  </div>
                  <span className="ml-2 text-yellow-800 font-bold text-xl">
                    MaxiCar
                  </span>
                </Link>
              </div>

            {/* Right side - Controls */}
            <div className="flex items-center space-x-4">
              {/* Notification Bell - Always show for non-driver users */}
              <div className="relative">
                <button
                  onClick={handleNotificationClick}
                  className="p-2 text-yellow-800 hover:bg-yellow-100 rounded-full transition-colors active:scale-95"
                  aria-label={user ? 'Notifications' : 'Login to view notifications'}
                  style={{
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)'
                  }}
                >
                  <FaBell className="w-6 h-6" />
                  {user && userNotifications.length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {userNotifications.length}
                    </span>
                  )}
                </button>
              </div>
              
              {/* Theme Toggle */}
              {/* Theme Toggle Switch */}
              <div className="flex items-center">
                <button
                  onClick={toggleTheme}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${isDark ? 'bg-yellow-500' : 'bg-gray-300'}`}
                  aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  <span
                    className={`${
                      isDark ? 'translate-x-5' : 'translate-x-1'
                    } inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform`}
                  />
                </button>
              </div>
              
              {/* Language Selector */}
              <div className="bg-white bg-opacity-80 rounded-full p-0.5">
                <LanguageSelector mobile={true} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Navigation */}
      <nav className={`fixed w-full z-50 ${isDark ? 'bg-gray-800' : 'bg-gradient-to-r from-yellow-200 to-yellow-300'} shadow-lg hidden md:block`}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="text-2xl font-bold flex items-center dark:text-white">
            <span className="mr-2">🚕</span>
            <span className="dark:text-white">{t('app_name')}</span>
          </Link>
          
          <div className="hidden md:flex space-x-8">
            <Link to="/" className="font-semibold hover:scale-105 transition-all duration-300 dark:text-gray-200 dark:hover:text-white text-gray-800 hover:text-black">
              {t('navigation.home')}
            </Link>
            <Link to="/services" className="font-semibold hover:scale-105 transition-all duration-300 dark:text-gray-200 dark:hover:text-white text-gray-800 hover:text-black">
              {t('navigation.services')}
            </Link>
            <Link to="/airport" className="font-semibold hover:scale-105 transition-all duration-300 dark:text-gray-200 dark:hover:text-white text-gray-800 hover:text-black">
              {t('navigation.airport_transfer')}
            </Link>
            <Link to="/contact" className="font-semibold hover:scale-105 transition-all duration-300 dark:text-gray-200 dark:hover:text-white text-gray-800 hover:text-black">
              {t('navigation.contact')}
            </Link>
            <Link to="/tracking" className="font-semibold hover:scale-105 transition-all duration-300 dark:text-gray-200 dark:hover:text-white text-gray-800 hover:text-black whitespace-nowrap">
              🗺️ {t('navigation.tracking')}
            </Link>
            <div className="flex items-center space-x-2">
              <Link to="/booking" className="flex flex-col items-center bg-black text-yellow-400 px-4 py-2 rounded-full hover:bg-gray-800 hover:scale-105 transition-all duration-300 font-bold">
                <FaTaxi className="text-lg" />
                <span className="text-xs">{t('navigation.book_now')}</span>
              </Link>
            </div>
          </div>

          <div className="hidden md:flex items-center space-x-4">
            {/* Notification Bell for Desktop */}
            {user && (
              <div className="relative">
                <button
                  onClick={handleNotificationClick}
                  className="p-2 text-gray-700 dark:text-gray-300 hover:bg-yellow-100 dark:hover:bg-gray-700 rounded-full transition-colors relative"
                  aria-label="Notifications"
                >
                  <FaBell className="w-5 h-5" />
                  {userNotifications.length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                      {userNotifications.length}
                    </span>
                  )}
                </button>
              </div>
            )}
            
            {/* Dark Mode Toggle Switch */}
            <div className="flex items-center">
              <button
                onClick={toggleTheme}
                type="button"
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 ${isDark ? 'bg-yellow-500' : 'bg-gray-200'}`}
                role="switch"
                aria-checked={isDark}
                aria-labelledby="dark-mode-toggle-label"
              >
                <span className="sr-only">Toggle dark mode</span>
                <span
                  aria-hidden="true"
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isDark ? 'translate-x-5' : 'translate-x-0'}`}
                >
                  {isDark ? (
                    <FaSun className="h-3 w-3 text-yellow-700 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
                  ) : (
                    <FaMoon className="h-3 w-3 text-gray-500 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
                  )}
                </span>
              </button>
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300" id="dark-mode-toggle-label">
                {isDark ? 'Dark' : 'Light'}
              </span>
            </div>
            
            <LanguageSelector />
            {!user ? (
              <Link to="/login" className="text-black dark:text-white font-semibold hover:text-gray-800 dark:hover:text-gray-200 hover:scale-105 transition-all duration-300">
                {t('navigation.login')}
              </Link>
            ) : (
              <div className="relative flex items-center">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 text-black font-medium hover:bg-gray-100 px-3 py-1.5 rounded-full transition-all duration-200"
                >
                  <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {user.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}
                  </div>
                  <div className="text-left">
                    <div className="text-sm font-medium">{user.name ? user.name.split(' ')[0] : 'User'}</div>
                    <div className="text-xs text-gray-500 -mt-0.5">Profile</div>
                  </div>
                  {isDriver && unreadCount > 0 && (
                    <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1 ml-1">
                      {unreadCount}
                    </span>
                  )}
                </button>
                {(!isDriver && userNotifications.length > 0) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowUserNotifications(true);
                    }}
                    className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    {userNotifications.length}
                  </button>
                )}
                {/* Desktop User Menu Dropdown */}
                {showUserMenu && (
                  <div className="absolute right-0 mt-3 w-64 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 py-2 shadow-lg z-50" style={{ top: 'calc(100% + 10px)' }}>
                    {user ? (
                      <div className="w-full">
                        <Link
                          to="/dashboard"
                          className="flex items-center px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-yellow-50 dark:hover:bg-gray-700 border-t border-gray-100 dark:border-gray-700"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <span className="w-5 mr-3">📊</span>
                          <span>Dashboard</span>
                        </Link>
                       
                        
                        {/* Messages for all users */}
                        <div className="relative">
                          <Link
                            to="/messages"
                            className="w-full flex items-center px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-yellow-50 dark:hover:bg-gray-700 border-t border-gray-100 dark:border-gray-700"
                            onClick={() => setShowUserMenu(false)}
                          >
                            <span className="w-5 mr-3">💬</span>
                            <span className="flex-1 text-left">Messages</span>
                            {userNotifications.length > 0 && (
                              <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5 min-w-[20px] flex items-center justify-center">
                                {userNotifications.length}
                              </span>
                            )}
                          </Link>
                        </div>

                        {/* Driver Notifications (separate from user messages) */}
                        {isDriver && (
                          <div className="relative">
                            <Link
                              to="/driver/notifications"
                              className="w-full flex items-center px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-yellow-50 dark:hover:bg-gray-700 border-t border-gray-100 dark:border-gray-700"
                              onClick={() => setShowUserMenu(false)}
                            >
                              <span className="w-5 mr-3">🔔</span>
                              <span className="flex-1 text-left">Driver Notifications</span>
                              {unreadCount > 0 && (
                                <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5 min-w-[20px] flex items-center justify-center">
                                  {unreadCount}
                                </span>
                              )}
                            </Link>
                          </div>
                        )}

                        {isDriver ? (
                          <button
                            onClick={() => {
                              handleDriverLogout();
                              setShowUserMenu(false);
                            }}
                            className="w-full flex items-center px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-yellow-50 dark:hover:bg-gray-700 border-t border-gray-100 dark:border-gray-700"
                          >
                            <span className="w-5 mr-3">🚗</span>
                            <span>{t('driver.logout')}</span>
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              setShowUserMenu(false);
                              setShowDriverModal(true);
                            }}
                            className="w-full flex items-center px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-yellow-50 dark:hover:bg-gray-700 border-t border-gray-100 dark:border-gray-700"
                          >
                            <span className="w-5 mr-3">🚕</span>
                            <span>{t('driver.login')}</span>
                          </button>
                        )}
                        <button
                          onClick={() => {
                            onLogout();
                            setShowUserMenu(false);
                          }}
                          className="w-full text-left px-4 py-3 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 border-t border-gray-100 dark:border-gray-700"
                        >
                          <span className="w-5 mr-3">🚪</span>
                          <span>{t('navigation.logout')}</span>
                        </button>
                      </div>
                    ) : (
                      <Link
                        to="/login"
                        className="flex items-center px-4 py-3 text-gray-700 hover:bg-gray-50"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <span className="w-5 mr-3">🔑</span>
                        <span>{t('navigation.login')}</span>
                      </Link>
                    )}
                  </div>
                )}
              </div>
            )}

            
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden text-black p-2 rounded-lg hover:bg-yellow-100 transition-colors"
            >
              <div className="w-6 h-6 flex flex-col justify-center space-y-1">
                <div className={`w-full h-0.5 bg-black transition-all ${isOpen ? 'rotate-45 translate-y-1' : ''}`}></div>
                <div className={`w-full h-0.5 bg-black transition-all ${isOpen ? 'opacity-0' : ''}`}></div>
                <div className={`w-full h-0.5 bg-black transition-all ${isOpen ? '-rotate-45 -translate-y-1' : ''}`}></div>
              </div>
            </button>
          </div>
        </div>
        
        {isOpen && (
          <div className="md:hidden pb-4 bg-white rounded-b-lg border-t border-gray-300 shadow-lg">
            <Link to="/" className="block py-3 px-4 text-black font-bold hover:text-gray-900 hover:bg-gray-100 transition-all">
              {t('navigation.home')}
            </Link>
            <Link to="/services" className="block py-3 px-4 text-black font-bold hover:text-gray-900 hover:bg-gray-100 transition-all">
              {t('navigation.services')}
            </Link>
            <Link to="/airport" className="block py-3 px-4 text-black font-bold hover:text-gray-900 hover:bg-gray-100 transition-all">
              {t('navigation.airport_transfer')}
            </Link>
            <Link to="/contact" className="block py-3 px-4 text-black font-bold hover:text-gray-900 hover:bg-gray-100 transition-all">
              {t('navigation.contact')}
            </Link>
            <Link to="/tracking" className="block py-3 px-4 text-black font-bold hover:text-gray-900 hover:bg-gray-100 transition-all">
              🗺️ {t('navigation.tracking')}
            </Link>
            {user && (
              <Link to="/messages" className="block py-3 px-4 text-black font-bold hover:text-gray-900 hover:bg-gray-100 transition-all">
                💬 Messages
                {userNotifications.length > 0 && (
                  <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-2 py-1">
                    {userNotifications.length}
                  </span>
                )}
              </Link>
            )}
            <Link to="/booking" className="block py-3 px-4 bg-black text-yellow-400 hover:bg-gray-800 transition-all font-bold rounded mx-4 text-center">
              {t('navigation.book_now')}
            </Link>

            {!user ? (
              <Link to="/login" className="block py-3 px-4 text-black font-bold hover:text-gray-900 hover:bg-gray-100 transition-all">
                {t('navigation.login')}
              </Link>
            ) : (
              <div className="px-4 py-3">
                {/* Notification Bell for Mobile */}
                {user && (
                  <div className="flex justify-between items-center mb-3 px-1">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-2">
                        {user.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}
                      </div>
                      <span className="font-semibold">{user.name ? user.name.split(' ')[0] : 'User'}</span>
                    </div>
                    <button 
                      onClick={() => {
                        navigate('/messages');
                        setIsOpen(false);
                      }}
                      className="relative p-2 rounded-full hover:bg-gray-100"
                      aria-label="Messages"
                    >
                      <FaBell className="text-xl text-gray-700" />
                      {userNotifications.length > 0 && (
                        <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                          {userNotifications.length}
                        </span>
                      )}
                    </button>
                  </div>
                )}
                <div className="flex items-center space-x-2 text-black font-bold mb-2">
                  <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center text-yellow-400 font-bold">
                    {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                  </div>
                  <span>{user.name || 'User'}</span>
                </div>
                <button
                  onClick={onLogout}
                  className="text-black font-bold hover:text-gray-900 transition-all"
                >
                  {t('navigation.logout')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
    
    {/* Notifications Modal - Rendered at root level */}
    <UserNotifications 
      userEmail={user?.email}
      isVisible={showUserNotifications}
      onClose={() => setShowUserNotifications(false)}
      notifications={userNotifications}
    />

    {/* Driver Login Modal - Moved outside nav for better visibility */}
    {showDriverModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
          <DriverLoginModal 
            isOpen={showDriverModal} 
            onClose={() => setShowDriverModal(false)}
            onLoginSuccess={handleDriverLogin}
          />
        </div>
      </div>
    )}

    {/* Mobile Bottom Navigation */}
    <div className="md:hidden fixed bottom-0 left-0 right-0 bg-gradient-to-br from-yellow-100 to-yellow-50 shadow-lg z-50 border-t-2 border-yellow-200">
      <div className="flex justify-around items-center h-18 px-2 py-1">
        {mobileNavItems.map((item, index) => {
          const isActive = item.to ? location.pathname === item.to : false;
          
          // Skip rendering if it's a button and we're in mobile view
          if (item.type === 'button') {
            return null;
          }
          
          // Handle user menu item
          if (item.type === 'user') {
            return (
              <div key="user-menu" className="relative flex-1">
                <div className="relative flex flex-col items-center" onClick={item.onClick}>
                  <div className="relative cursor-pointer">
                    {user ? (
                      <div className="w-8 h-8 rounded-full bg-yellow-500 flex items-center justify-center text-white font-bold">
                        {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                      </div>
                    ) : (
                      <FaUserCircle className="text-2xl text-yellow-600" />
                    )}
                    {(!isDriver && userNotifications.length > 0) && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                        {userNotifications.length}
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] mt-0.5 font-medium text-gray-600">
                    {user ? 'Profile' : 'Login'}
                  </span>
                </div>
                
                {/* User Menu Dropdown */}
                {showUserMenu && user && (
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-56 bg-white rounded-lg shadow-xl z-50 border border-gray-200 overflow-hidden">
                    {/* Airport Transfer Link */}
                    <Link
                      to="/airport-transfer"
                      className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-yellow-50 border-b border-gray-100"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <span className="w-5 mr-3">✈️</span>
                      <span>Airport Transfer</span>
                    </Link>
                    
                    {/* Driver Notifications Link - Only show when driver is logged in */}
                    {isDriver && (
                      <Link
                        to="/driver/notifications"
                        className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-yellow-50 border-b border-gray-100"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <span className="w-5 mr-3">🔔</span>
                        <span>Driver Notifications</span>
                        {unreadCount > 0 && (
                          <span className="ml-auto bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                            {unreadCount}
                          </span>
                        )}
                      </Link>
                    )}
                    
                    {/* Contact Page Link */}
                    <Link
                      to="/contact"
                      className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-yellow-50 border-b border-gray-100"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <span className="w-5 mr-3">📞</span>
                      <span>Contact Us</span>
                    </Link>
                    
                    {/* Driver Login/Logout Button */}
                    <button
                      onClick={() => {
                        if (isDriver) {
                          handleDriverLogout();
                        } else {
                          setShowDriverModal(true);
                        }
                        setShowUserMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 text-sm text-blue-600 hover:bg-blue-50 border-b border-gray-100 flex items-center"
                    >
                      <span className="w-5 mr-3">{isDriver ? '🚕' : '🚗'}</span>
                      <span>{isDriver ? 'Logout as Driver' : 'Login as Driver'}</span>
                    </button>
                    
                    {/* Logout Button */}
                    <button
                      onClick={() => {
                        onLogout();
                        setShowUserMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 flex items-center"
                    >
                      <span className="w-5 mr-3">🚪</span>
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            );
          }
          
          return (
            <Link
              key={item.to}
              to={item.to}
              className={`flex-1 flex flex-col items-center justify-center py-2 ${
                isActive ? 'text-yellow-500' : 'text-gray-500'
              }`}
              aria-label={item.label}
              onClick={() => setIsOpen(false)}
            >
              {item.isPrimary ? (
                <div className="bg-yellow-500 text-white rounded-full w-12 h-12 flex items-center justify-center -mt-6 shadow-lg">
                  <span className="text-2xl font-bold">{item.icon}</span>
                </div>
              ) : (
                <>
                  <div className="text-2xl">
                    {isActive ? item.activeIcon || item.icon : item.icon}
                  </div>
                  <span className="text-[10px] mt-0.5 font-medium">{item.label}</span>
                  {isActive && (
                    <div className="w-1 h-1 bg-yellow-500 rounded-full mt-1"></div>
                  )}
                </>
              )}
            </Link>
          );
        })}
      </div>
    </div>

    {/* Driver Login Modal */}
    {showDriverModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
          <DriverLoginModal 
            isOpen={showDriverModal} 
            onClose={() => setShowDriverModal(false)}
            onLoginSuccess={handleDriverLogin}
          />
        </div>
      </div>
    )}

    {/* Notifications Modal - Rendered at root level */}
    <UserNotifications 
      userEmail={user?.email}
      isVisible={showUserNotifications}
      onClose={() => setShowUserNotifications(false)}
      notifications={userNotifications}
    />
  </nav>
</div>
  );
};

Navbar.propTypes = {
  user: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  onLogout: PropTypes.func.isRequired,
  theme: PropTypes.oneOf(['light', 'dark']),
  toggleTheme: PropTypes.func
};

export default Navbar;